import React from 'react';
import { twMerge } from 'tailwind-merge';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import MeetingMemberAvatar from 'flow/components/meeting/shared/MeetingMemberAvatar';
import { selectFullscreenOrNarrowWindow } from 'flow/selectors/meeting-selectors';
import { useAppSelector } from 'flow/hooks/useRedux';
import useMeetingMemberGuestBadge from 'flow/hooks/useMeetingMemberGuestBadge';
import {
    MOCK_COLORS,
    MOCK_PARTICIPANTS_ENABLED,
} from 'flow/utils/meeting-mock';
import PinOverlayButton from '../overlays/PinOverlayButton';
import RemoteVideoLabels from '../overlays/RemoteVideoLabels';
import {
    getInnerContainerClass,
    getOuterContainerClass,
} from '../remote-video-classes';
import { ChatUserKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { MeetingMember } from 'flow/types/Meeting';

interface TileDialInParticipantProps {
    member: TypedMap<MeetingMember>;
    number?: string;
    /** The roster member's chatUserKey, if they are a colleague (VID-264). */
    chatUserKey?: ChatUserKey;
    isCompact?: boolean;
    showPinControl?: boolean;
    pinFeedName?: string;
}

const noop = () => {};

/**
 * A dial-in (phone) participant tile: avatar + labels only, with no WebRTC
 * peer, stream, or video element — these participants joined by phone and have
 * no video member. Split out from TileVideoParticipant, which is purely a video
 * member.
 */
const TileDialInParticipant: React.FC<TileDialInParticipantProps> = ({
    member,
    number,
    chatUserKey,
    isCompact = false,
    showPinControl = false,
    pinFeedName,
}) => {
    const isFullscreenOrNarrow = useAppSelector(selectFullscreenOrNarrowWindow);
    // One memoized selector instance per tile — see TileVideoParticipant: the
    // old in-selector creation defeated reselect, rerunning the full contact
    // aggregation on every dispatch.
    const guestBadge = useMeetingMemberGuestBadge(member.get('displayName'));

    const rawName = member.get('displayName');
    const displayName = rawName
        ? sliceUUIDFromUserName(rawName)
        : member.get('callerId');

    const showPinButton = showPinControl && !!pinFeedName;

    // Dev-only: flat-colour mock participant tiles so the packing is readable.
    const mockBackground =
        MOCK_PARTICIPANTS_ENABLED && rawName && MOCK_COLORS[rawName]
            ? { backgroundColor: MOCK_COLORS[rawName] }
            : undefined;

    return (
        <div className={twMerge('group', getOuterContainerClass())}>
            {showPinButton && pinFeedName && (
                <PinOverlayButton feedName={pinFeedName} />
            )}
            <div
                className={twMerge(
                    getInnerContainerClass(isFullscreenOrNarrow),
                    // Dial-in has no video: a neutral card, and the tile the
                    // avatar measures itself against.
                    'meeting-tile-avatar-container bg-gray-800',
                )}
                style={mockBackground}
            >
                <MeetingMemberAvatar
                    hasVideo={false}
                    name={rawName || displayName || ''}
                    isCaller={true}
                    chatUserKey={chatUserKey}
                    size="meetingTile"
                    phoneNumber={number}
                />
                <RemoteVideoLabels
                    displayName={displayName}
                    guestBadge={guestBadge}
                    isCameraFeed={true}
                    isCaller={true}
                    playAudio={true}
                    showVideo={false}
                    peerSetupAttempts={0}
                    isCompact={isCompact}
                    onRestartVideo={noop}
                />
            </div>
        </div>
    );
};

// Memoized for the same reason as TileVideoParticipant: stable per-participant
// props mean a feed reorder or another member's change skips this tile's body.
export default React.memo(TileDialInParticipant);
